<template>
  <div class="add-pet-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'AddPetPage_phone.png' : 'AddPetPage.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Add Your Pet</h1>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="page-wrapper">
      <div class="form-card">

        <div class="form-group">
          <label class="form-label">Pet's Name</label>
          <input v-model="pet.pet_name" type="text" class="form-input" placeholder="e.g. Buddy" required />
        </div>

        <div class="form-group">
          <label class="form-label">Breed</label>
          <select v-model="selectedBreed" class="form-input form-select">
            <option disabled value="">Select a breed</option>
            <option v-for="breed in breeds" :key="breed.id" :value="breed.name">{{ breed.name }}</option>
            <option value="custom">Add my pet's breed</option>
          </select>
        </div>

        <div class="form-group" v-if="selectedBreed === 'custom'">
          <label class="form-label">New Breed Name</label>
          <div class="inline-row">
            <input type="text" v-model="newBreed" placeholder="Enter breed name" class="form-input" />
            <button @click="addNewBreed" :disabled="!newBreed" class="inline-btn" type="button">Add</button>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Age</label>
          <div class="pill-group">
            <label
              v-for="option in ['Puppy', 'Adult', 'Senior']"
              :key="option"
              class="pill-option"
              :class="{ 'pill-active': pet.age === option }"
            >
              <input type="radio" v-model="pet.age" :value="option" hidden />
              {{ option }}
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Gender</label>
          <div class="pill-group">
            <label class="pill-option pill-male" :class="{ 'pill-active': pet.gender === 'Male' }">
              <input type="radio" v-model="pet.gender" value="Male" hidden />
              Male
            </label>
            <label class="pill-option pill-female" :class="{ 'pill-active': pet.gender === 'Female' }">
              <input type="radio" v-model="pet.gender" value="Female" hidden />
              Female
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            Description
            <button type="button" @click="generateDescription" class="ai-btn">✨ Generate with AI</button>
          </label>
          <textarea v-model="pet.description" class="form-textarea" placeholder="Describe your pet..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Pet Photo</label>
          <input ref="fileInput" type="file" accept="image/*" @change="handleFileUpload" class="file-input" />
        </div>

        <button
          class="submit-btn"
          type="button"
          :disabled="isLoading"
          @click="addPet"
        >
          <span v-if="isLoading">Adding...</span>
          <span v-else>Add Pet</span>
        </button>

        <div v-if="successMessage" class="feedback success">{{ successMessage }}</div>
        <div v-if="errorMessage" class="feedback error">{{ errorMessage }}</div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      pet: {
        pet_name: '',
        breed: '',
        other_breed: '',
        age: '',
        gender: '',
        description: '',
        photo: null,
        isOnline: true,
        location: ''
      },
      isLoading: false,
      isAuthenticated: false,
      breeds: [],
      selectedBreed: '',
      newBreed: '',
      sidebarOpen: false,
      successMessage: '',
      errorMessage: '',
      petsOpen: false,
    };
  },
  created() {
    this.fetchBreeds();
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async fetchBreeds() {
      const res = await axios.get(`${backendUrl}/api/breeds/breeds`, { withCredentials: true });
      this.breeds = res.data;
    },

    async addNewBreed() {
      if (!this.newBreed) return;
      try {
        const res = await axios.post(`${backendUrl}/api/breeds/breeds`, { name: this.newBreed }, { withCredentials: true });
        this.breeds.push(res.data);
        this.selectedBreed = this.newBreed;
        this.newBreed = '';
      } catch (err) {
        console.log('Could not add breed: ' + err.response?.data?.error || err.message);
      }
    },

    async generateDescription() {
      const name = this.pet.pet_name;
      const age = this.getAgeBracket(this.pet.age);
      const gender = this.pet.gender;
      const selected = this.selectedBreed;
      const custom = this.newBreed.trim();
      const breed = selected === 'custom' ? custom : selected;

      if (!name || !breed || !age || !gender) {
        this.errorMessage = 'Please fill in name, breed, age, and gender first.';
        return;
      }

      try {
        const response = await axios.post(
          `${backendUrl}/api/pets/generate-description`,
          {
            pet_name: name,
            selectedBreed: selected,
            newBreed: custom,
            age: age,
            gender: gender
          },
          { withCredentials: true }
        );
        this.pet.description = response.data.description;
      } catch (error) {
        console.error('Error generating description:', error);
      }
    },

    getAgeBracket(age) {
      if (age === 'Puppy') return 'Puppy';
      if (age === 'Adult') return 'Adult';
      if (age === 'Senior') return 'Senior';
      return '';
    },

    async addPet() {
      this.successMessage = '';
      this.errorMessage = '';

      const finalBreed = this.selectedBreed === 'custom'
        ? this.newBreed.trim()
        : this.selectedBreed;

      if (!finalBreed) {
        this.errorMessage = 'Please pick or enter a breed.';
        return;
      }

      this.pet.breed = finalBreed;

      if (!this.pet.pet_name || !finalBreed || !this.pet.age || !this.pet.gender || !this.pet.description || !this.pet.photo) {
        this.errorMessage = 'Please fill in all the details.';
        return;
      }

      if (this.pet.photo && !this.pet.photo.type.startsWith('image/')) {
        this.errorMessage = 'Please upload a valid image file.';
        return;
      }

      try {
        this.isLoading = true;

        const formData = new FormData();
        formData.append('pet_name', this.pet.pet_name);
        formData.append('breed', finalBreed);
        formData.append('custom_breed', this.newBreed.trim());
        formData.append('age', this.pet.age);
        formData.append('gender', this.pet.gender);
        formData.append('description', this.pet.description);
        formData.append('photo', this.pet.photo);
        formData.append('isOnline', this.pet.isOnline);

        const response = await axios.post(
          `${backendUrl}/api/pets/add-pet`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          }
        );

        console.log('✅ Pet added:', response.data);
        this.successMessage = 'Pet added successfully!';
        this.resetForm();

      } catch (err) {
        console.error('❌ Error adding pet:', err);
        this.errorMessage = 'Error adding pet. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.pet = {
        pet_name: '',
        breed: '',
        age: '',
        gender: '',
        description: '',
        photo: null,
        isOnline: true
      };
      this.selectedBreed = '';
      this.newBreed = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      this.pet.photo = file;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.add-pet-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-wrapper {
  max-width: 760px;
  margin: 0 auto;
  padding: 14px 24px 40px;  /* more bottom padding */
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.form-card {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f0e0ff;
  padding: 20px 26px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.form-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23aaa' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}

.inline-row {
  display: flex;
  gap: 10px;
}

.inline-row .form-input { flex: 1; }

.inline-btn {
  padding: 0 16px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 11px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s;
  white-space: nowrap;
}

.inline-btn:hover { background: #ba71ff; color: #fff; }
.inline-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.pill-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.pill-option {
  flex: 1;
  text-align: center;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.pill-option:hover { border-color: #ba71ff; color: #4a1070; }

.pill-option.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-male.pill-active  { background: #eff6ff; border-color: #2563eb; color: #1d4ed8; }
.pill-female.pill-active { background: #fdf2f8; border-color: #db2777; color: #be185d; }

.form-textarea {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  color: #1a1a1a;
  transition: all 0.2s ease;
}

.form-textarea:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.ai-btn {
  padding: 4px 10px;
  background: #fffbe8;
  border: 1px solid #f6d860;
  border-radius: 999px;
  color: #92610a;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.ai-btn:hover { background: #fff3b0; }

.file-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px dashed #f0e0ff;
  border-radius: 11px;
  font-size: 13px;
  background: #fffbe8;
  cursor: pointer;
  color: #888;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: #ba71ff;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
}

.submit-btn {
  width: 100%;
  padding: 11px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 4px;
  font-family: inherit;
}

.submit-btn:hover { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.feedback {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}

.success { background: #d4f5e2; color: #1a6b3c; }
.error   { background: #fde8e8; color: #991b1b; }

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

/* ── Desktop: 2 column grid ── */
@media (min-width: 600px) {
  .form-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 28px;
  }

  .form-group:has(.form-textarea),
  .form-group:has(.file-input),
  .checkbox-row,
  .submit-btn,
  .feedback {
    grid-column: 1 / -1;
  }

  .form-input { padding: 16px 18px; font-size: 16px; border-radius: 14px; }
  .form-textarea { padding: 16px 18px; font-size: 16px; min-height: 180px; }  /* much taller */
  .form-label { font-size: 13px; margin-bottom: 8px; }
  .form-group { margin-bottom: 26px; }  /* much more spacing between fields */
  .pill-option { padding: 12px 20px; font-size: 14px; }
  .submit-btn { padding: 18px; font-size: 17px; margin-top: 12px; }
  .checkbox-label { font-size: 15px; }
  .inline-btn { padding: 0 24px; font-size: 15px; border-radius: 14px; }
  .form-card { padding: 28px 32px; }  /* more padding inside card */
  .page-wrapper { padding: 18px 24px 60px; }  /* more bottom space */
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .page-wrapper { padding: 12px 12px 24px; }  /* a bit more bottom */
  .form-card { padding: 12px 14px; }  /* slightly more room */
  .form-group { margin-bottom: 10px; }  /* slightly more spacing */
  .form-label { font-size: 9px; margin-bottom: 3px; }
  .form-input { padding: 7px 10px; font-size: 12px; border-radius: 8px; }
  .form-textarea { padding: 7px 10px; font-size: 12px; min-height: 60px; border-radius: 8px; }  /* taller */
  .pill-option { padding: 6px 10px; font-size: 11px; }
  .checkbox-label { font-size: 12px; }
  .submit-btn { padding: 10px; font-size: 12px; margin-top: 6px; }
  .ai-btn { font-size: 9px; padding: 3px 8px; }
  .inline-btn { font-size: 11px; border-radius: 8px; }
  .file-input { padding: 7px 10px; font-size: 12px; border-radius: 8px; }
}
</style>